import { useEffect } from "react";
import { caseRef } from "../format";
import type { Citation } from "../types";

/** Which part of the judgment an excerpt is from (backend sections.py). */
const PART: Record<string, string> = {
  intro: "partea introductivă",
  facts: "redarea cauzei: instanțele inferioare și părțile",
  law: "normele și hotărârile citate de CSJ",
  reasoning: "motivarea CSJ",
  ruling: "dispozitivul hotărârii",
  guidance: "hotărâre explicativă a Plenului CSJ",
  unknown: "parte nestabilită",
};

/**
 * Parts in which the CSJ is not the one speaking. A decision retells the
 * courts below in the same present tense it reasons in, so an excerpt from
 * there reads like the Supreme Court's view unless the panel says it is not.
 */
const NOT_THE_CSJ: Record<string, string> = {
  facts:
    "Fragmentul redă hotărârile instanțelor inferioare sau susținerile părților, " +
    "nu motivarea CSJ.",
  unknown:
    "Nu s-a putut stabili dacă fragmentul este motivarea CSJ sau redarea " +
    "instanțelor inferioare. Verificați la sursă.",
};

/** The line the backend writes where a new part of the judgment begins. */
const MARK = /^\[— de aici: (.+) —\]$/;

/**
 * A cited CSJ decision: which court, when, the excerpt the answer was given,
 * and the court's own copy one click away.
 *
 * Deliberately not the decision itself (decided 4 November 2026). The texts
 * are held for search, not re-hosted: a lawyer checks a citation against the
 * signed copy on the court's site, and any name the anonymiser missed stays
 * inside one passage rather than a whole judgment on this screen.
 *
 * Same frame as the act viewer, so a decision and a statute open in the same
 * place - but labelled court practice throughout, never "în vigoare".
 */
export function DecisionPanel({
  citation,
  onClose,
}: {
  citation: Citation;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // The excerpt arrives as the matching chunk with the end of the one before
  // and the start of the one after, blank-line separated, with a marked line
  // wherever a new part of the judgment begins.
  const paragraphs = citation.snippet.split(/\n{2,}/).filter((p) => p.trim());
  const part = citation.part ? PART[citation.part] : null;
  const warn = citation.part ? NOT_THE_CSJ[citation.part] : undefined;

  return (
    <aside className="src" aria-label="Hotărârea citată">
      <div className="src-head">
        <div className="src-id">
          <h2>{caseRef(citation)}</h2>
          <div className="src-sub">
            <span className="stat info">practică judiciară</span>
            <span>{citation.title}</span>
          </div>
        </div>
        <button className="close" onClick={onClose} aria-label="Închide sursa">
          ×
        </button>
      </div>

      {warn && paragraphs.length > 0 ? (
        <p className="src-warn">{warn}</p>
      ) : (
        <p className="case-note">
          În Republica Moldova practica judiciară este orientativă, nu obligatorie.
        </p>
      )}

      <div className="src-body">
        {paragraphs.length > 0 ? (
          <>
            <p className="case-label">
              Fragmentul găsit de căutare{part ? ` · din ${part}` : ""} · anonimizat
            </p>
            <div className="case-passage">
              {paragraphs.map((p, i) => {
                const mark = MARK.exec(p.trim());
                return mark ? (
                  <p key={i} className="case-mark">
                    de aici: {mark[1]}
                  </p>
                ) : (
                  <p key={i}>{p}</p>
                );
              })}
            </div>
          </>
        ) : (
          // Read whole by the model, so which part the answer leans on is
          // not known - better to say so than to highlight a guess.
          <p className="muted">
            Hotărârea a fost citită integral pentru acest răspuns. Textul ei se
            consultă la sursă.
          </p>
        )}
      </div>

      <div className="src-foot">
        {citation.url && (
          <a className="ghost" href={citation.url} target="_blank" rel="noreferrer">
            Deschide hotărârea pe csj.md
          </a>
        )}
      </div>
    </aside>
  );
}
